<template>
  <div class="werkwijze-page">

    <div class="page-header">
      <div class="page-header-grid"></div>
      <div class="container page-header-content">
        <router-link to="/" class="back-link">
          <i class="fas fa-arrow-left"></i> {{ $t('werkwijze.back') }}
        </router-link>
        <h1>{{ $t('werkwijze.title') }}</h1>
        <p>{{ $t('werkwijze.subtitle') }}</p>
      </div>
    </div>

    <div class="steps-section">
      <div class="container">
        <div class="timeline">

          <div
            v-for="(step, i) in steps"
            :key="i"
            class="timeline-item"
            :class="i % 2 === 0 ? 'timeline-item--left' : 'timeline-item--right'"
            v-reveal="i * 150"
          >
            <div class="timeline-connector">
              <div class="timeline-dot" :class="`dot--${step.color}`">
                <i :class="step.icon"></i>
              </div>
              <div v-if="i < steps.length - 1" class="timeline-line"></div>
            </div>

            <div class="timeline-card" :class="`card--${step.color}`">
              <div class="timeline-step-num">{{ String(i + 1).padStart(2, '0') }}</div>
              <h2>{{ $t(step.titleKey) }}</h2>
              <p>{{ $t(step.descKey) }}</p>
              <div class="timeline-deliverable">
                <i class="fas fa-arrow-right"></i>
                {{ $t(step.deliverableKey) }}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <div class="cta-section">
      <div class="container">
        <div class="cta-card" v-reveal>
          <div class="cta-icon">
            <i class="fas fa-comments"></i>
          </div>
          <h2>{{ $t('werkwijze.cta_title') }}</h2>
          <p>{{ $t('werkwijze.cta_desc') }}</p>
          <router-link to="/#contact" class="btn-cta">
            {{ $t('werkwijze.cta_button') }} <i class="fas fa-arrow-right"></i>
          </router-link>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
const steps = [
  {
    titleKey: 'werkwijze.step1_title',
    descKey: 'werkwijze.step1_desc',
    deliverableKey: 'werkwijze.step1_deliverable',
    icon: 'fas fa-comments',
    color: 'blue',
  },
  {
    titleKey: 'werkwijze.step2_title',
    descKey: 'werkwijze.step2_desc',
    deliverableKey: 'werkwijze.step2_deliverable',
    icon: 'fas fa-clipboard-list',
    color: 'purple',
  },
  {
    titleKey: 'werkwijze.step3_title',
    descKey: 'werkwijze.step3_desc',
    deliverableKey: 'werkwijze.step3_deliverable',
    icon: 'fas fa-laptop-code',
    color: 'green',
  },
  {
    titleKey: 'werkwijze.step4_title',
    descKey: 'werkwijze.step4_desc',
    deliverableKey: 'werkwijze.step4_deliverable',
    icon: 'fas fa-rocket',
    color: 'orange',
  },
]
</script>

<style scoped>
/* ─── Page header ────────────────────────────── */
.page-header {
  background-color: transparent;
  padding: 8rem 0 4rem;
  position: relative;
  overflow: hidden;
}

.page-header-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
}

.page-header-content { position: relative; z-index: 1; }

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.45);
  text-decoration: none;
  margin-bottom: 1.75rem;
  transition: color 0.2s;
}

.back-link:hover { color: rgba(255, 255, 255, 0.8); text-decoration: none; }

.page-header h1 {
  font-family: 'Jersey 15', sans-serif;
  font-size: clamp(2.25rem, 5vw, 3.5rem);
  color: #fff;
  font-weight: 400;
  margin-bottom: 0.75rem;
  letter-spacing: 0;
}

.page-header p { color: rgba(255, 255, 255, 0.5); font-size: 1.05rem; margin: 0; }

/* ─── Timeline ───────────────────────────────── */
.steps-section {
  padding: 5rem 0 4rem;
  background: linear-gradient(180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.55) 14%,
    rgba(0, 0, 0, 0.55) 100%);
}

.timeline {
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.timeline-item {
  display: flex;
  gap: 0;
  align-items: stretch;
}

/* Connector (dot + line) */
.timeline-connector {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 56px;
}

.timeline-dot {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
  z-index: 1;
  transition: transform 0.2s, box-shadow 0.2s;
}

.timeline-item:hover .timeline-dot {
  transform: scale(1.1);
}

.dot--blue,
.dot--purple,
.dot--green,
.dot--orange {
  background: var(--accent-tint);
  color: var(--accent);
  border: 2px solid var(--accent-line);
  box-shadow: 0 0 0 4px var(--accent-tint), 0 0 24px var(--accent-tint);
}

.timeline-line {
  width: 2px;
  flex: 1;
  background: linear-gradient(to bottom, var(--accent-line), var(--border));
  margin: 4px 0;
  min-height: 32px;
}

/* Card */
.timeline-card {
  flex: 1;
  background: linear-gradient(180deg, #151515, #0e0e0e);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  padding: 1.875rem 2rem;
  margin-left: 1.5rem;
  margin-bottom: 1rem;
  position: relative;
  overflow: hidden;
  transition: transform 0.25s, box-shadow 0.25s, border-color 0.25s;
  box-shadow: 0 14px 44px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.timeline-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  border-radius: 3px 0 0 3px;
  opacity: 0;
  transition: opacity 0.3s;
}

.card--blue::before,
.card--purple::before,
.card--green::before,
.card--orange::before { background: var(--accent-grad); }

.timeline-item:hover .timeline-card {
  transform: translateX(4px);
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.5), 0 0 30px var(--accent-tint);
  border-color: var(--accent-line);
}

.timeline-item:hover .timeline-card::before {
  opacity: 1;
}

.timeline-step-num {
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: 3.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.06);
  line-height: 1;
  letter-spacing: -2px;
  position: absolute;
  top: 0.75rem;
  right: 1.25rem;
  pointer-events: none;
  user-select: none;
}

.timeline-card h2 {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 0.75rem;
}

.timeline-card p {
  font-size: 0.95rem;
  color: var(--text-muted);
  line-height: 1.75;
  margin-bottom: 1.25rem;
}

.timeline-deliverable {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--accent);
  background: var(--accent-tint);
  border: 1px solid var(--accent-line);
  border-radius: 8px;
  padding: 0.45rem 0.875rem;
}

.timeline-deliverable i { font-size: 0.7rem; }

/* ─── CTA ────────────────────────────────────── */
.cta-section {
  padding: 0 0 5rem;
  background: linear-gradient(180deg,
    rgba(0, 0, 0, 0.55) 0%,
    rgba(0, 0, 0, 0) 100%);
}

.cta-card {
  background: linear-gradient(160deg, #151515, #0e0e0e);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  padding: 4rem 3.5rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 14px 44px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.cta-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--accent-grad);
}

.cta-card::after {
  content: '';
  position: absolute;
  top: -40%;
  left: 50%;
  width: 420px;
  height: 320px;
  transform: translateX(-50%);
  background: radial-gradient(closest-side, var(--accent-tint), transparent 72%);
  filter: blur(40px);
  pointer-events: none;
}

.cta-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--accent-tint);
  border: 1px solid var(--accent-line);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: var(--accent);
  margin: 0 auto 1.5rem;
  position: relative;
  z-index: 1;
  box-shadow: 0 0 30px var(--accent-tint);
}

.cta-card h2 {
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: clamp(1.5rem, 3vw, 2rem);
  color: #fff;
  font-weight: 700;
  margin-bottom: 0.875rem;
  letter-spacing: -0.5px;
}

.cta-card p {
  color: rgba(255, 255, 255, 0.5);
  font-size: 1rem;
  margin-bottom: 2rem;
}

.cta-card h2, .cta-card p { position: relative; z-index: 1; }

.btn-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  background: var(--accent-grad);
  color: #0a0a0a;
  padding: 0.9rem 2.1rem;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 700;
  text-decoration: none;
  transition: filter 0.2s, transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 6px 30px var(--accent-glow);
  position: relative;
  z-index: 1;
}

.btn-cta:hover {
  filter: brightness(1.06);
  color: #0a0a0a;
  transform: translateY(-2px);
  box-shadow: 0 10px 40px var(--accent-glow);
  text-decoration: none;
}

.btn-cta i { transition: transform 0.2s; }
.btn-cta:hover i { transform: translateX(3px); }

/* ─── Responsive ─────────────────────────────── */
@media (max-width: 767px) {
  .page-header { padding: 6rem 0 3rem; }
  .steps-section { padding: 3.5rem 0 3rem; }
  .timeline-connector { width: 36px; }
  .timeline-dot { width: 36px; height: 36px; font-size: 0.9rem; }
  .timeline-card { margin-left: 1rem; padding: 1.5rem; }
  .timeline-step-num { font-size: 2.5rem; }
  .cta-card { padding: 2.5rem 1.5rem; }
}
</style>
